/**
 * Seasons, from a real annual temperature cycle rather than from the calendar.
 *
 * Each place gets a temperature curve — its annual mean plus a cosine of its
 * seasonal swing, warmest around 20 July in the north and 20 January in the
 * south — and everything seasonal is read off that curve the way nature reads
 * it:
 *
 * - **Snow** lies when the month is cold enough, so Swedish winters are white
 *   and Roman ones are not, and the snow line on a mountain moves with the
 *   seasons.
 * - **Leaves** come out when spring warms past 6 °C, colour when autumn cools
 *   through 11 °C and fall below 5 °C. On the equator, where the curve barely
 *   moves, trees stay green all year.
 * - **Grass** goes dormant and straw-coloured in the cold months.
 *
 * The southern hemisphere needs no special case: its curve simply peaks in
 * January.
 */

import type { Climate } from '../biome/climate.js';
import { smoothstep } from '../biome/climate.js';

/** Day of year, 0-based, in UTC. */
export function dayOfYear(date: Date): number {
  const start = Date.UTC(date.getUTCFullYear(), 0, 1);
  return (date.getTime() - start) / 86_400_000;
}

const YEAR = 365.2422;
/** Warmest day of the year, north and south, lagging the solstice by ~4 weeks. */
const PEAK_NORTH = 200;
const PEAK_SOUTH = 17;

function phaseOf(day: number, lat: number): number {
  const peak = lat >= 0 ? PEAK_NORTH : PEAK_SOUTH;
  return ((day - peak) / YEAR) * Math.PI * 2;
}

/** Monthly-mean temperature on a given day, °C. */
export function seasonalTemperature(climate: Climate, lat: number, day: number): number {
  return climate.temperature + climate.seasonality * Math.cos(phaseOf(day, lat));
}

/** The climate as it is on a given day: annual mean replaced by the day's. */
export function onDay(climate: Climate, lat: number, day: number): Climate {
  return { ...climate, temperature: seasonalTemperature(climate, lat, day) };
}

export interface SeasonState {
  name: 'spring' | 'summer' | 'autumn' | 'winter';
  /** Deciduous foliage, 0 bare .. 1 full leaf. */
  leaves: number;
  /** Share of deciduous leaves in autumn colour, 0..1. */
  autumn: number;
  /** Grass vigour, 0 dormant .. 1 lush. */
  greenness: number;
  /** The day's temperature, °C. */
  temperature: number;
}

export function seasonState(climate: Climate, lat: number, day: number): SeasonState {
  const t = seasonalTemperature(climate, lat, day);
  // Warming or cooling: the derivative of the cosine.
  const warming = -Math.sin(phaseOf(day, lat)) > 0;
  const swingy = smoothstep(2, 4, climate.seasonality);

  let leaves: number;
  let autumn = 0;
  if (warming) {
    leaves = smoothstep(5, 9, t);
  } else {
    leaves = smoothstep(3, 6.5, t);
    autumn = (1 - smoothstep(7, 12, t)) * leaves;
  }
  // Where the year hardly swings, deciduous trees keep their leaves.
  leaves = 1 - (1 - leaves) * swingy;
  autumn *= swingy;

  const greenness = 1 - (1 - smoothstep(1, 8, t)) * swingy;

  // Meteorological season by hemisphere, for naming.
  const month = new Date(Date.UTC(2001, 0, 1) + day * 86_400_000).getUTCMonth();
  const northern = ['winter', 'winter', 'spring', 'spring', 'spring', 'summer',
    'summer', 'summer', 'autumn', 'autumn', 'autumn', 'winter'] as const;
  const name = lat >= 0 ? northern[month] : northern[(month + 6) % 12];

  return { name, leaves, autumn, greenness, temperature: t };
}
